import { chunk, each, go, tap } from "fxjs/es";
import {
  getPaperPathFromSVGpathData,
  getPaperScope,
  createCircle,
  markSingleStar,
  getMinimumPoint,
  markCircle,
  pathOffsetting_adv,
} from "../PaperInterface/paper_interfaces.js";

export class KeyringHook {
  constructor({
    art_board_size,
    path_data,
    radius = { inner: 10, outer: 20 },
    default_positioning = "auto",
    is_append_to_DOM = false,
  }) {
    this.original_path_data = path_data;
    this.paper_path = {};
    this.inside_tangent_path = {};
    this.outside_tangent_path = {};
    this.art_board_size = art_board_size;
    this.radius = radius;
    this.is_append_to_DOM = is_append_to_DOM;
    this.paper_scope = getPaperScope({
      art_board_size,
      is_append_to_DOM,
      is_retina_dpi: true,
    });
    this.default_positioning = ["auto", "outside"].includes(default_positioning)
      ? default_positioning
      : "auto";
    this.current_positioning_mode = default_positioning;
    this.init_center_position = { x: 0, y: 0 };
    this.last_cursor_point = { x: 0, y: 0 };
    this.last_center_position = { x: 0, y: 0 };
    this.last_hook_united_path_data = null;
    this.is_last_positioining_inside = false;
    this.init(path_data);
    this.outer_circle = createCircle({
      paper_scope: this.paper_scope,
      radius: radius.outer,
      name: "tangent_outer_circle",
      init_position: this.init_center_position,
      insert: true,
    });
    this.inner_circle = createCircle({
      paper_scope: this.paper_scope,
      radius: radius.inner,
      name: "tangent_inner_circle",
      init_position: this.init_center_position,
      insert: true,
    });
  }
  init(path_data) {
    this.original_path_data = path_data;
    this.paper_path = getPaperPathFromSVGpathData({
      paper_scope: this.paper_scope,
      path_data,
      name: "keyring_path",
    });
    this.inside_tangent_path = pathOffsetting_adv({
      paper_path: this.paper_path,
      offset_size: -this.radius.outer,
      name: "inside_tangent_path",
    });
    this.outside_tangent_path = pathOffsetting_adv({
      paper_path: this.paper_path,
      offset_size: this.radius.inner,
      name: "outside_tangent_path",
    });
    this.last_center_position = this.init_center_position =
      this.getHookPositionTopCenterOutsidePath();
    this.current_positioning_mode = this.default_positioning;
    // this.paper_scope.view.element.style.left = `${-this.art_board_size
    //   .margin}px`;
    // this.paper_scope.view.element.style.top = `${-this.art_board_size
    //   .margin}px`;
    process.env.NODE_ENV === "development" && (window.__kh = this);
  }
  changePathData(new_path_data) {
    this.init(new_path_data);
  }
  toggleDetailView(is_detail_view, art_board_el, path_el) {
    const paper_canvas = this.paper_scope.view.element;
    if (is_detail_view) {
      art_board_el.style.backgroundColor = "transparent";
      path_el.style.visibility = "hidden";
      paper_canvas.style.display = "flex";
    } else {
      art_board_el.style.backgroundColor = "#f3f3f3";
      path_el.style.visibility = "visible";
      paper_canvas.style.display = "none";
    }
  }
  changeArtBoardViewPosition({ left, top }) {
    Object.assign(this.paper_scope.view.element.style, {
      left: `${left}px`,
      top: `${top}px`,
    });
  }
  updateHookPositioningMode(positioning = "auto") {
    positioning = ["inside", "outside", "auto"].includes(positioning)
      ? positioning
      : "auto";
    this.current_positioning_mode = positioning;
  }
  getHookPositionTopCenterOutsidePath() {
    const { centerX: path_center_x } = this.outside_tangent_path.bounds;
    const height_margin = this.art_board_size.height * 0.25;
    const vertical_bisect_line = new this.paper_scope.Path.Line({
      from: [path_center_x, -height_margin],
      to: [path_center_x, this.art_board_size.height + height_margin],
    });
    const itx_top_center_outside_location = go(
      this.outside_tangent_path.getIntersections(vertical_bisect_line),
      (bisect_itx) =>
        getMinimumPoint({
          intersections_arr: bisect_itx,
          target_coord: "y",
        })
    );
    const point = itx_top_center_outside_location?.point;
    //outside path 보다 살짝 상단에 위치해야 outCircle 로 인식
    return { x: point?.x, y: point?.y - 0.0001 };
  }
  isHookPositioningInside({ point }) {
    let positioning;
    if (this.current_positioning_mode === "auto") {
      if (this.outside_tangent_path.contains(point)) {
        if (this.inside_tangent_path.contains(point)) {
          positioning = true;
        } else {
          positioning = this.is_last_positioining_inside;
        }
      } else {
        positioning = false;
      }
    } else {
      positioning = this.current_positioning_mode === "inside";
    }
    return positioning;
  }
  updateTangentCirclePosition(paper_point) {
    this.inner_circle.position = this.outer_circle.position = paper_point;
  }

  getHookCenter(cursor_point) {
    /*
     * INPUT: cursor_point = { x: Number, y: Number }   (cursor_point MUST BE referenced at art board el (LEFT, TOP) as (0, 0))
     * OUTPUT: center_position = { x: Number, y: Number }
     * */
    const click_point = (this.last_cursor_point = new this.paper_scope.Point(
      cursor_point.x,
      cursor_point.y
    ));

    const is_positioning_inside = (this.is_last_positioining_inside =
      this.isHookPositioningInside({ point: cursor_point }));

    const target_tangent_path = is_positioning_inside
      ? this.inside_tangent_path
      : this.outside_tangent_path;

    const current_nn_location =
      target_tangent_path.getNearestLocation(click_point);

    this.updateTangentCirclePosition(current_nn_location.point);

    this.is_append_to_DOM &&
      markSingleStar({
        paper_scope: this.paper_scope,
        paper_point: current_nn_location.point,
      });

    return (this.last_center_position = {
      x: current_nn_location.point.x,
      y: current_nn_location.point.y,
    });
  }

  getPathDataHookUnited(cursor_point) {
    /*
        INPUT: cursor_point = { x: Number, y: Number }
        OUTPUT: pathdata string (original path 와 hook path 통합)
    */
    this.getHookCenter(cursor_point || this.last_center_position);
    const new_path = new this.paper_scope.CompoundPath({
      children: [
        this.is_last_positioining_inside
          ? this.paper_path.clone({ deep: false, insert: false })
          : this.paper_path.unite(this.outer_circle, { insert: false }),
        this.inner_circle.clone({ deep: false, insert: false }),
      ],
    });
    const removed_small_area_path = this.#removeSmallAreaPaths(new_path, 3); // 키링의 inner path 면적이 약 7
    return (this.last_hook_united_path_data = removed_small_area_path.pathData);
  }

  #removeSmallAreaPaths(compound_path, area_threshold) {
    const new_cpd_path = compound_path.clone({ deep: true, insert: false });

    for (let i = new_cpd_path.children.length - 1; i >= 0; i--) {
      let path = new_cpd_path.children[i];
      let area = Math.abs(path.area);
      if (area <= area_threshold) {
        path.remove();
      }
    }
    return new_cpd_path;
  }

  getOverlapCurveLength() {
    // let subtract_path = this.outer_circle.subtract(this.paper_path, {
    //   insert: false,
    //   trace: true,
    // });
    // subtract_path = this.outer_circle.subtract(subtract_path, {
    //   insert: false,
    // });
    // subtract_path.set({
    //   strokeColor: "green",
    //   fillColor: "transparent",
    //   strokeWidth: 2,
    // });
    //
    // const res = go(
    //   subtract_path.curves,
    //   filter((c) => {
    //     const median_location = c.getLocationAtTime(0.5);
    //     const distance_from_center =
    //       this.outer_circle.bounds.center.getDistance(median_location.point);
    //     return distance_from_center < this.radius.outer - 1;
    //   })
    // );
    //
    // subtract_path.name = "sub";
    // const exist = this.outer_circle.layer.getItem({ name: "sub" });
    // if (exist) {
    //   exist.remove();
    // }
    // layer.addChild(subtract_path);
    let overlap_len = 0;
    if (process.env.NODE_ENV === "development") {
      const layer = this.outer_circle.layer;
      layer.getItems({ name: "circle_mark" }).forEach((x) => x.remove());
      go(
        this.paper_path.getIntersections(this.outer_circle),
        tap(
          each((cl) =>
            markCircle({
              paper_scope: this.paper_scope,
              point: cl.point,
              name: "circle_mark",
            })
          )
        ),
        chunk(2),
        each(([cl1, cl2]) => {
          cl2 && (overlap_len += cl1.point.getDistance(cl2.point));
        })
      );

      return overlap_len;
    } else {
      go(
        this.paper_path.getIntersections(this.outer_circle),
        chunk(2),
        each(([cl1, cl2]) => {
          cl2 && (overlap_len += cl1.point.getDistance(cl2.point));
        })
      );
      return overlap_len;
    }
  }
}

/*
 * TODO - short term
 *  1. 마우스를 뗏을 때 닿는 길이가 일정 이하일 때 길이를 return 하는 method 를 만들고 => alert 기능
 *  2. 안쪽으로 절대 들어갈 수 없는 간격인 경우 에러, 바깥쪽으로 절대 나갈 수 없는 간격인 경우 에러
 * */
